import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { fabric } from 'fabric';
import './ProductColor.css';
import close from '../assets/close.png';

const API_URL = 'http://127.0.0.1:8000/process-upload';

const UploadImageComponent = ({
  canvas,
  setSelectedImageColors,
  setIsPng,
  calculatePrice,
  closePropertiesSidebar,
  imageFile,
  setImageFile,
}) => {
  const [uploading, setUploading] = useState(false);
  const btnRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const lastImageFile = useRef(null);

  // -----------------------------
  // Helpers: channel conversions
  // -----------------------------
  const rgbToBgr = (c) => {
    // supports [r,g,b] or [r,g,b,a]
    if (!Array.isArray(c)) return c;
    if (c.length === 4) return [c[2], c[1], c[0], c[3]];
    return [c[2], c[1], c[0]];
  };

  const cssFromRGB = (rgba) => {
    // rgba is [r,g,b,a] with a=0..255 or undefined
    if (!Array.isArray(rgba)) return 'rgba(0,0,0,1)';
    const [r, g, b, a = 255] = rgba;
    return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
  };

  const cssFromBGR = (bgra) => {
    // bgra is [b,g,r,a] with a=0..255 or undefined
    if (!Array.isArray(bgra)) return 'rgba(0,0,0,1)';
    const [b, g, r, a = 255] = bgra;
    return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
  };

  // --- util: detect solid vs transparent alpha channels in the uploaded image ---
  function hasAlpha(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onerror = reject;
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          const ctx = c.getContext('2d', { willReadFrequently: true });
          c.width = img.width;
          c.height = img.height;
          ctx.drawImage(img, 0, 0);
          const data = ctx.getImageData(0, 0, c.width, c.height).data;
          let isSolidBackground = true;
          for (let i = 3; i < data.length; i += 4) {
            if (data[i] < 255) {
              isSolidBackground = false;
              break;
            }
          }
          resolve(isSolidBackground); // true = fully opaque (no alpha < 255)
        } catch (e) {
          // if canvas taints (e.g., cross-origin), assume solid to avoid breaking flow
          resolve(true);
        }
      };
    });
  }

  // When parent sets imageFile programmatically
  useEffect(() => {
    if (!lastImageFile.current && !imageFile) return;
    if (imageFile && lastImageFile.current !== imageFile) {
      lastImageFile.current = imageFile;
      sendImageToApi(API_URL, imageFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageFile]);

  // Parse /process-upload payload (expects merged_json_content.samples_merged with rgb + weight)
// Parse /process-upload payload
function parseProcessUploadPayload(data) {
  const first = data?.results?.[0] || {};
  const merged = first?.merged_json_content || {};

  // samples array
  const samples = Array.isArray(merged?.samples_merged) ? merged.samples_merged : [];

  // normalize [r,g,b] -> [r,g,b,a]
  const toRGBA = (arr) => (Array.isArray(arr) ? (arr.length === 3 ? [...arr, 255] : arr) : null);

  // palette (RGBA)
  const paletteColors = samples
    .map((s) => toRGBA(s?.rgb))
    .filter(Boolean);

  // dominant color (max weight) -> fallback to first -> fallback [0,0,0,255]
  const dominantEntry = samples.reduce(
    (best, cur) => (!best || (cur?.weight || 0) > (best?.weight || 0) ? cur : best),
    null
  );
  const dominantColor = toRGBA(dominantEntry?.rgb) || paletteColors[0] || [0, 0, 0, 255];

  // image data url
  const imageDataUrl = merged?.original_image_base64 || first?.original_image_base64 || '';

  // session id
  const sessionId = first?.session_id || merged?.session_id || null;

  // 🔑 classification can be on the top-level result OR inside merged_json_content (depending on backend)
  const classification =
    first?.classification ??
    merged?.classification ??
    data?.classification ??
    null;

  // BGR(A) copies
  const rgbToBgr = (c) => (Array.isArray(c) ? [c[2], c[1], c[0], c[3] ?? 255] : c);
  const paletteColorsBGR = paletteColors.map(rgbToBgr);
  const dominantColorBGR = rgbToBgr(dominantColor);

  return {
    paletteColors,        // RGBA
    dominantColor,        // RGBA
    paletteColorsBGR,     // BGRA
    dominantColorBGR,     // BGRA
    imageDataUrl,
    sessionId,
    classification,       // ✅ now returned
  };
}

  // Send image to API and place on Fabric canvas
const sendImageToApi = async (apiUrl, file) => {
  // helper (local) to make CSS rgba from RGB[A]
  const cssFromRGB = (rgba) => {
    if (!Array.isArray(rgba)) return 'rgba(0,0,0,1)';
    const [r, g, b, a = 255] = rgba;
    return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
  };

  try {
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('include_merged_json', 'true');

    const isSolidBackground = await hasAlpha(file);

    const response = await axios.post(apiUrl, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (response.status === 200) {
      const {
        paletteColors,
        dominantColor,
        paletteColorsBGR,
        dominantColorBGR,
        imageDataUrl,
        sessionId,
        classification, // make sure parseProcessUploadPayload returns this
      } = parseProcessUploadPayload(response.data);
      console.log("RAW API response:", response.data);
console.log("classification →", classification);

      // Decide "Full Color" vs "Spot Color"
  const isFullColor = /^Full\s*Color$/i.test(classification || '');
      console.log('isFullColor',isFullColor);

      // UI swatches (RGB → CSS). If Full Color, empty = show "Full Color" in UI.
      setSelectedImageColors?.(isFullColor ? [] : paletteColors.map(cssFromRGB));

      // Tell parent if file is PNG
      setIsPng?.(file.type?.toLowerCase() === 'image/png');

      // Build fabric image from data URL
      if (!imageDataUrl) {
        console.warn('No image data URL returned from API.');
        return;
      }

      const imgObj = new Image();
      imgObj.crossOrigin = 'Anonymous';
      imgObj.src = imageDataUrl;

      imgObj.onload = () => {
        const designArea = canvas?.getObjects()?.find((o) => o.designArea === true);
        if (!designArea) {
          console.warn('No designArea object found on canvas.');
          return;
        }

        // Positioning: left/top/width in % of designArea
        const leftPercent = 0;
        const topPercent = 0;
        const widthPercent = 40;

        const left = designArea.left + (designArea.width * leftPercent) / 100;
        const top = designArea.top + (designArea.height * topPercent) / 100;
        const targetWidth = designArea.width * (widthPercent / 100);
        const scaleFactor = targetWidth / imgObj.width;

        const img = new fabric.Image(imgObj, {
          left,
          top,
          scaleX: scaleFactor,
          scaleY: scaleFactor,
          selectable: true,
          designElement: true,

          // store BGR(A) internally (empty if Full Color)
          paletteColors: isFullColor ? [] : paletteColorsBGR,
          dominant_color: isFullColor ? null : dominantColorBGR,

          // keep classification flags for later logic/UI
          classification,
          isFullColor,

          // misc custom props
          imageFile: isSolidBackground, // true => fully opaque (no alpha)
          imageType: file.type,
          singleColor: { r: 0, g: 0, b: 0 },
          singleColorMode: false,
          makeColor: { r: 0, g: 0, b: 0 },
          makeColorMode: false,
          bgRemove: false,
          session_id: sessionId,

          leftPercent,
          topPercent,
          widthPercent,
          heightPercent: ((imgObj.height * scaleFactor) / designArea.height) * 100,
        });

        img.setCoords();
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        calculatePrice?.(canvas);
      };

      imgObj.onerror = (e) => {
        console.error('Failed to load image from data URL', e);
      };
    }
  } catch (err) {
    console.error('Error uploading image:', err);
  } finally {
    setUploading(false);
    // reset the externally controlled file ref to allow same-file re-upload
    lastImageFile.current = null;
    setTimeout(() => setImageFile?.(null), 100);
  }
};



  // Handlers
  const changeImage = async (e) => {
    const file = e.target.files?.[0];
    if (file) await sendImageToApi(API_URL, file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) changeImage({ target: { files: [file] } });
  };

  const handleClick = () => btnRef.current?.click();

  return (
    <>
      {uploading && (
        <div className="uploading-popup-overlay">
          <div className="uploading-popup-contents">
            <div className="spinner"></div>
            <p className="upload-spinner-text">
              Please wait! We’re mixing up the colors just for you!
            </p>
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '3px',
          background: '#f6f6f6',
        }}
      >
        <h5
          style={{
            margin: 0,
            color: '#211e1eaa',
            fontWeight: 400,
            textAlign: 'center',
            flexGrow: 1,
          }}
        >
          Choose file
        </h5>
        <button
          onClick={closePropertiesSidebar}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
        >
          <img src={close} alt="Close" className="closeicon" />
        </button>
      </div>

      <div className="highart">
        <p className="highfont">High resolution artwork will look the best.</p>
      </div>

      <div
        className={`upload-container ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <div className="upload-box">
          <div className="upload-icon">{/* svg omitted */}</div>
          <p className="upload-text">
            Drag &amp; Drop or <br />
            <span className="browse-text">Browse</span> Your Computer
          </p>
          <p className="file-types">JPG, PNG, EMF, JFIF, BMP and PDF (Max 5 MB)</p>
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.bmp,.jfif,.pdf,.emf"
            ref={btnRef}
            className="file-input"
            onChange={changeImage}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </>
  );
};

export default UploadImageComponent;
